import { Box, Collapse, Typography, useTheme } from '@mui/material';
import {
  CaretDown,
  CaretRight,
  Folder as FolderIcon,
  type Icon,
} from '@phosphor-icons/react';
import React, { useState } from 'react';
import { useDarkMode } from '../../../../hooks/useDarkMode';

interface CollapsibleGroupProps {
  title: string;
  defaultExpanded?: boolean;
  icon?: Icon | React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}

export const CollapsibleGroup: React.FC<CollapsibleGroupProps> = ({
  title,
  defaultExpanded = false,
  icon,
  action,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const palette = theme.customColorPalette;
  const isDarkMode = useDarkMode();
  const textColor = isDarkMode ? palette.gray[300] : palette.gray[600];

  const renderedIcon = !icon ? (
    <FolderIcon size={16} color={textColor} />
  ) : React.isValidElement(icon) ? (
    icon
  ) : (
    React.createElement(icon as Icon, { size: 16, color: textColor })
  );

  return (
    <Box>
      <Box
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px',
          cursor: 'pointer',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: isDarkMode ? palette.gray[800] : palette.blue[50],
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            minWidth: 0,
          }}
        >
          {renderedIcon}
          <Typography
            sx={{
              color: textColor,
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: textColor,
          }}
        >
          {isHovered && action}
          {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </Box>
      </Box>
      <Collapse in={isOpen}>
        <Box sx={{ marginLeft: '8px' }}>{children}</Box>
      </Collapse>
    </Box>
  );
};
